import { type FC, lazy, Suspense } from "react"

import type { LandingPageHeroBlock } from "@forento/core/models/landing"
import { cn } from "@forento/ui/utilities/tailwind"

import VideoPreview from "./VideoPreview"

const RichTextPreview = lazy(() => import("@forento/ui/components/RichTextPreview"))

type Props = { block: LandingPageHeroBlock }
const HeroBlockPreview: FC<Props> = ({ block }) => {
	const splitMedia =
		block.content.media ?? (block.content.image ? { type: "image" as const, ...block.content.image } : null)

	if (block.content.layout === "split") {
		return (
			<section className="from-landing-surface-muted via-landing-background to-landing-surface-muted relative isolate overflow-hidden bg-linear-to-b px-6 py-24 sm:py-32 lg:px-8">
				<div className="absolute inset-x-0 -top-40 -z-10 transform-gpu overflow-hidden blur-3xl sm:-top-80">
					<div
						className="landing-primary-gradient relative left-[calc(50%-11rem)] aspect-video w-xl -translate-x-1/2 rotate-30 opacity-20 sm:left-[calc(50%-30rem)] sm:w-6xl"
						style={{
							clipPath:
								"polygon(74.1% 44.1%, 100% 61.6%, 97.5% 26.9%, 85.5% 0.1%, 80.7% 2%, 72.5% 32.5%, 60.2% 62.4%, 52.4% 68.1%, 47.5% 58.3%, 45.2% 34.5%, 27.5% 76.7%, 0.1% 64.9%, 17.9% 100%, 27.6% 76.8%, 76.1% 97.7%, 74.1% 44.1%)",
						}}
					/>
				</div>
				<div className="relative mx-auto max-w-7xl">
					<div className="grid gap-8 lg:grid-cols-2 lg:items-center lg:gap-12">
						<div className="flex flex-col justify-center">
							<h1 className="text-landing-foreground text-4xl font-bold tracking-tight sm:text-5xl lg:text-6xl">
								{block.content.title || "Hero Title"}
							</h1>
							<Suspense>
								<RichTextPreview
									value={block.content.description}
									fallback="Hero description"
									className="text-landing-foreground-muted mt-6 text-lg leading-8 sm:text-xl [&>p]:mb-4 [&>p:last-child]:mb-0"
								/>
							</Suspense>

							{block.content.button && (
								<div className="mt-10">
									<span className="landing-button inline-flex items-center justify-center px-6 py-3 text-base font-semibold">
										{block.content.button.label}
									</span>
								</div>
							)}
						</div>
						{splitMedia && (
							<div className="border-landing-border/50 bg-landing-surface-muted aspect-video w-full overflow-hidden rounded-lg border">
								{splitMedia.type === "image" ? (
									<img
										src={splitMedia.url}
										alt={splitMedia.alt ?? ""}
										className="size-full object-cover"
									/>
								) : (
									<VideoPreview
										type={splitMedia.type}
										videoId={splitMedia.videoId}
										className="h-full"
									/>
								)}
							</div>
						)}
					</div>
				</div>
				<div className="absolute inset-x-0 top-[calc(100%-13rem)] -z-10 transform-gpu overflow-hidden blur-3xl sm:top-[calc(100%-30rem)]">
					<div
						className="landing-primary-gradient relative left-[calc(50%+3rem)] aspect-video w-xl -translate-x-1/2 opacity-20 sm:left-[calc(50%+36rem)] sm:w-6xl"
						style={{
							clipPath:
								"polygon(74.1% 44.1%, 100% 61.6%, 97.5% 26.9%, 85.5% 0.1%, 80.7% 2%, 72.5% 32.5%, 60.2% 62.4%, 52.4% 34.5%, 47.5% 58.3%, 45.2% 34.5%, 27.5% 76.7%, 0.1% 64.9%, 17.9% 100%, 27.6% 76.8%, 76.1% 97.7%, 74.1% 44.1%)",
						}}
					/>
				</div>
			</section>
		)
	}

	return (
		<section
			className={cn(
				"relative isolate overflow-hidden px-6 py-24 sm:py-32 lg:px-8",
				!block.content.backgroundMedia &&
					"from-landing-surface-muted via-landing-background to-landing-surface-muted bg-linear-to-b",
			)}
		>
			{block.content.backgroundMedia ? (
				<>
					<div className="absolute inset-0 -z-10">
						{block.content.backgroundMedia.type === "image" ? (
							<img src={block.content.backgroundMedia.url} alt="" className="size-full object-cover" />
						) : (
							<video
								src={block.content.backgroundMedia.url}
								autoPlay
								muted
								loop
								playsInline
								className="size-full object-cover"
							/>
						)}
					</div>
					<div className="absolute inset-0 -z-10 bg-black/40" />
				</>
			) : (
				<>
					<div className="absolute inset-x-0 -top-40 -z-10 transform-gpu overflow-hidden blur-3xl sm:-top-80">
						<div
							className="landing-primary-gradient relative left-[calc(50%-11rem)] aspect-video w-xl -translate-x-1/2 rotate-30 opacity-20 sm:left-[calc(50%-30rem)] sm:w-6xl"
							style={{
								clipPath:
									"polygon(74.1% 44.1%, 100% 61.6%, 97.5% 26.9%, 85.5% 0.1%, 80.7% 2%, 72.5% 32.5%, 60.2% 62.4%, 52.4% 68.1%, 47.5% 58.3%, 45.2% 34.5%, 27.5% 76.7%, 0.1% 64.9%, 17.9% 100%, 27.6% 76.8%, 76.1% 97.7%, 74.1% 44.1%)",
							}}
						/>
					</div>
					<div className="absolute inset-x-0 top-[calc(100%-13rem)] -z-10 transform-gpu overflow-hidden blur-3xl sm:top-[calc(100%-30rem)]">
						<div
							className="landing-primary-gradient relative left-[calc(50%+3rem)] aspect-video w-xl -translate-x-1/2 opacity-20 sm:left-[calc(50%+36rem)] sm:w-6xl"
							style={{
								clipPath:
									"polygon(74.1% 44.1%, 100% 61.6%, 97.5% 26.9%, 85.5% 0.1%, 80.7% 2%, 72.5% 32.5%, 60.2% 62.4%, 52.4% 34.5%, 47.5% 58.3%, 45.2% 34.5%, 27.5% 76.7%, 0.1% 64.9%, 17.9% 100%, 27.6% 76.8%, 76.1% 97.7%, 74.1% 44.1%)",
							}}
						/>
					</div>
				</>
			)}
			<div className="relative mx-auto max-w-4xl text-center">
				<h1
					className={cn(
						"text-5xl font-bold tracking-tight sm:text-6xl lg:text-7xl",
						block.content.backgroundMedia ? "text-white" : "text-landing-foreground",
					)}
				>
					{block.content.title || "Hero Title"}
				</h1>
				<Suspense>
					<RichTextPreview
						value={block.content.description}
						fallback="Hero description"
						className={cn(
							"mt-6 text-lg leading-8 sm:text-xl [&>p]:mb-4 [&>p:last-child]:mb-0",
							block.content.backgroundMedia ? "text-white" : "text-landing-foreground-muted",
						)}
					/>
				</Suspense>
				{block.content.button && (
					<div className="mt-10 flex items-center justify-center">
						<span className="landing-button px-6 py-3 text-base font-semibold">
							{block.content.button.label}
						</span>
					</div>
				)}
			</div>
		</section>
	)
}

export default HeroBlockPreview
